import { FC, ReactNode } from 'react';

interface ConfirmModalProps {
  open: boolean;
  title: string;
  /** O que quebra ao confirmar. Nunca deixar em branco numa exclusão. */
  children: ReactNode;
  confirmLabel?: string;
  tone?: 'danger' | 'accent';
  isBusy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmação com impacto declarado. Substitui `window.confirm`, que não deixa
 * dizer o que vai quebrar — e nas exclusões deste módulo é justamente o impacto
 * ("está no roteiro de N produtos") que decide se a pessoa continua.
 */
const ConfirmModal: FC<ConfirmModalProps> = ({
  open,
  title,
  children,
  confirmLabel = 'Confirmar',
  tone = 'danger',
  isBusy = false,
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;
  const confirmClasses =
    tone === 'danger'
      ? 'bg-theme-danger/90 text-accent-contrast hover:bg-theme-danger' // navy no escuro, branco no claro
      : 'bg-accent text-accent-contrast hover:bg-accent-hover';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        data-testid="seop-confirm-modal"
        className="w-full max-w-lg rounded-xl border border-theme-border bg-surface-card p-6"
      >
        <h2 className="text-lg font-semibold text-content-primary">{title}</h2>
        <div className="mt-3 space-y-2 text-sm text-content-secondary">{children}</div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-theme-border px-4 py-2 text-sm text-content-secondary hover:text-content-primary"
          >
            Cancelar
          </button>
          <button
            type="button"
            data-testid="seop-confirm-ok"
            disabled={isBusy}
            onClick={onConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50 ${confirmClasses}`}
          >
            {isBusy ? 'Aguarde…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
